<template>
<div class="cs-page">
  <div class="cs-toolbar no-print">
    <button class="btn btn-sm btn-success" @click="doPrint"><i class="mdi mdi-printer me-1"></i>Print / Save as PDF</button>
    <button class="btn btn-sm btn-outline-secondary ms-2" @click="$router.go(-1)"><i class="mdi mdi-arrow-left me-1"></i>Back</button>
  </div>

  <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

  <div v-else-if="stmt" class="cs-doc">

    <header class="cs-head">
      <div class="cs-brand-block">
        <img src="/assets/images/flockr_logo.png" alt="Flockr" class="cs-brand-logo" />
        <div>
          <div class="cs-farm">FLOCKR CREDIT PASSPORT — STATEMENT</div>
          <div class="cs-meta">Flockr Africa · flockr.africa · An ODPC-licensed Data Controller (Kenya)</div>
          <div class="cs-meta">Generated {{ fmtDateTime(new Date().toISOString()) }}</div>
        </div>
      </div>
      <div class="cs-title-block">
        <div class="cs-score-big" :style="{color: tierColour}">{{ stmt.passport.score }}</div>
        <div class="cs-tier" :style="{background: tierColour}">{{ stmt.passport.tier }}</div>
        <div class="cs-meta cs-meta-sm">Score out of 1000 · as at {{ fmtDate(stmt.passport.computed_at) }}</div>
      </div>
    </header>

    <div class="cs-holder">
      <div><span class="cs-l">Passport Holder</span><strong>{{ holderName }}</strong></div>
      <div><span class="cs-l">Phone</span><strong>{{ phoneMasked }}</strong></div>
      <div><span class="cs-l">Statement Period</span><strong>{{ fmtDate(stmt.from) }} — {{ fmtDate(stmt.to) }}</strong></div>
      <div><span class="cs-l">KYC Status</span><strong>{{ kycStatus }}</strong></div>
      <div><span class="cs-l">Member Since</span><strong>{{ memberSince }}</strong></div>
      <div><span class="cs-l">Statement ID</span><strong>{{ statementId }}</strong></div>
    </div>

    <h3 class="cs-section">1. Credit Passport Summary</h3>
    <table class="cs-mini">
      <tr v-for="p in stmt.passport.pillars" :key="p.code">
        <td style="width:32%"><strong>{{ p.label }}</strong> <span style="color:#6B6356;">({{ p.weight_pct }}%)</span></td>
        <td style="width:20%">{{ p.score }} / {{ p.max }}</td>
        <td>
          <div class="cs-bar"><div class="cs-bar-fill" :style="{width: (p.score/p.max*100) + '%', background: tierColour}"></div></div>
        </td>
      </tr>
    </table>

    <div class="cs-highlights">
      <div><span class="cs-l">Lifetime GMV</span><strong>KES {{ fmt(stmt.passport.stats.gmv_kes) }}</strong></div>
      <div><span class="cs-l">Orders (last 12 mo)</span><strong>{{ stmt.passport.stats.orders_12mo }}</strong></div>
      <div><span class="cs-l">Payment Success</span><strong>{{ stmt.passport.stats.stk_success_pct !== null ? stmt.passport.stats.stk_success_pct + '%' : '—' }}</strong></div>
      <div><span class="cs-l">Avg. Rating</span><strong>{{ stmt.passport.stats.avg_rating || '—' }} / 5</strong></div>
      <div><span class="cs-l">Disputes Lost</span><strong>{{ stmt.passport.stats.dispute_losses }}</strong></div>
      <div><span class="cs-l">Referrals Active</span><strong>{{ stmt.passport.stats.referrals_txn }}</strong></div>
    </div>

    <h3 class="cs-section">2. Transaction History ({{ stmt.orders.length }} orders)</h3>
    <table class="cs-data">
      <thead><tr>
        <th>#</th><th>Date</th><th>Order No.</th><th>Role</th><th>Counterparty</th><th>Listing</th><th class="text-end">Amount</th><th>Status</th>
      </tr></thead>
      <tbody>
        <tr v-if="!stmt.orders.length"><td colspan="8" style="text-align:center;color:#6B6356;padding:16px;">No transactions in this period</td></tr>
        <tr v-for="(o, i) in stmt.orders" :key="'o'+o.id">
          <td>{{ i+1 }}</td>
          <td>{{ fmtDate(o.created_at) }}</td>
          <td>{{ o.order_number }}</td>
          <td style="text-transform:capitalize;">{{ o.role }}</td>
          <td>{{ o.role === 'buyer' ? (o.seller_name || '—') : (o.buyer_name || '—') }}</td>
          <td>{{ o.listing_title || '—' }}</td>
          <td class="text-end">{{ (o.currency || 'KES') }} {{ fmt(o.total_amount) }}</td>
          <td><span :class="statusClass(o.status)">{{ o.status }}</span></td>
        </tr>
      </tbody>
    </table>

    <h3 class="cs-section">3. Wallet Activity ({{ stmt.wallet.length }} entries)</h3>
    <table class="cs-data">
      <thead><tr>
        <th>Date</th><th>Type</th><th>Category</th><th>Description</th><th class="text-end">Amount</th><th class="text-end">Balance</th>
      </tr></thead>
      <tbody>
        <tr v-if="!stmt.wallet.length"><td colspan="6" style="text-align:center;color:#6B6356;padding:16px;">No wallet activity in this period</td></tr>
        <tr v-for="w in stmt.wallet" :key="'w'+w.id">
          <td>{{ fmtDate(w.created_at) }}</td>
          <td style="text-transform:capitalize;">{{ w.type }}</td>
          <td style="text-transform:capitalize;">{{ w.category }}</td>
          <td>{{ w.description }}</td>
          <td class="text-end" :style="{color: w.type === 'credit' ? '#16a34a' : '#dc2626'}">
            {{ w.type === 'credit' ? '+' : '−' }} {{ fmt(w.amount) }}
          </td>
          <td class="text-end">{{ fmt(w.balance_after) }}</td>
        </tr>
      </tbody>
    </table>

    <h3 v-if="stmt.vet_payments && stmt.vet_payments.length" class="cs-section">4. Vet Consultations Paid</h3>
    <table v-if="stmt.vet_payments && stmt.vet_payments.length" class="cs-data">
      <thead><tr><th>Date</th><th>Veterinary Officer</th><th class="text-end">Amount</th><th>Status</th></tr></thead>
      <tbody>
        <tr v-for="v in stmt.vet_payments" :key="'v'+v.id">
          <td>{{ fmtDate(v.created_at) }}</td>
          <td>{{ v.vet_name || '—' }}</td>
          <td class="text-end">KES {{ fmt(v.amount) }}</td>
          <td>{{ v.status }}</td>
        </tr>
      </tbody>
    </table>

    <h3 class="cs-section">{{ (stmt.vet_payments && stmt.vet_payments.length) ? '5' : '4' }}. Farm Snapshot</h3>
    <table class="cs-mini">
      <tr>
        <td><strong>Registered Farms</strong><span class="cs-fill">{{ stmt.farms.length }}</span></td>
        <td><strong>Total Animals</strong><span class="cs-fill">{{ stmt.animal_totals ? stmt.animal_totals.total : 0 }}</span></td>
        <td><strong>Active Herd</strong><span class="cs-fill">{{ stmt.animal_totals ? stmt.animal_totals.active_count : 0 }}</span></td>
        <td><strong>Sold to Date</strong><span class="cs-fill">{{ stmt.animal_totals ? stmt.animal_totals.sold_count : 0 }}</span></td>
      </tr>
    </table>
    <table v-if="stmt.farms.length" class="cs-data mt-2">
      <thead><tr><th>Farm</th><th>Type</th><th>County</th><th>Registered</th></tr></thead>
      <tbody>
        <tr v-for="fr in stmt.farms" :key="'f'+fr.id">
          <td>{{ fr.name }}</td>
          <td>{{ fr.farm_type }}</td>
          <td>{{ fr.county }}</td>
          <td>{{ fmtDate(fr.created_at) }}</td>
        </tr>
      </tbody>
    </table>

    <div class="cs-attest">
      <div class="cs-attest-body">
        <p><strong>Statement Authenticity.</strong> This statement is generated directly from the Flockr platform database. To verify, scan the QR code or visit the URL below — the live Passport will match this statement's score.</p>
        <p style="margin:0;font-size:11px;color:#6B6356;">Flockr Africa is licensed by the Office of the Data Protection Commissioner (ODPC), Republic of Kenya. This statement is issued at the request of the passport holder and may be presented to any lending institution.</p>
      </div>
      <div class="cs-qr">
        <img v-if="verifyUrl" :src="qrUrl" alt="Scan to verify" />
        <div class="cs-qr-hint">Scan to verify</div>
      </div>
    </div>

    <footer class="cs-foot">
      <div>Flockr Africa · flockr.africa · support@flockr.africa</div>
      <div>{{ verifyUrl }}</div>
    </footer>

  </div>
</div>
</template>

<script>
/* eslint-disable */
import { execute, flocker } from '@/api';

export default {
  name: 'CreditStatementPrint',
  data() {
    return {
      loading: true,
      stmt: null,
      shareToken: null
    };
  },
  computed: {
    tierColour() { return this.stmt && this.stmt.passport ? this.stmt.passport.tier_colour : '#BFA164'; },
    holderName() {
      var u = sessionStorage.getItem('userDisplayName') || sessionStorage.getItem('firstName') || 'Passport Holder';
      return u;
    },
    phoneMasked() {
      var p = sessionStorage.getItem('phone') || '';
      return p.replace(/(\d{3})\d{4}(\d{2})/, '$1****$2');
    },
    kycStatus() {
      if (!this.stmt || !this.stmt.passport) return '—';
      var identity = (this.stmt.passport.pillars || []).find(function(p){ return p.code === 'identity'; });
      if (!identity) return '—';
      return identity.score >= 90 ? 'Fully Verified' : identity.score >= 30 ? 'Partially Verified' : 'Not Verified';
    },
    memberSince() {
      if (!this.stmt || !this.stmt.passport) return '—';
      var d = this.stmt.passport.stats.account_age_days;
      var since = new Date();
      since.setDate(since.getDate() - d);
      return since.toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' });
    },
    statementId() {
      var uid = (sessionStorage.getItem('userId') || '').substr(0, 8);
      var ts = new Date().toISOString().replace(/\D/g,'').substr(0,14);
      return 'FCP-' + uid.toUpperCase() + '-' + ts;
    },
    verifyUrl() {
      return this.shareToken ? (window.location.origin + '/credit-passport/view/' + this.shareToken) : '';
    },
    qrUrl() {
      if (!this.verifyUrl) return '';
      return 'https://api.qrserver.com/v1/create-qr-code/?size=140x140&margin=6&data=' + encodeURIComponent(this.verifyUrl);
    }
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try {
        var params = new URLSearchParams(window.location.search);
        var from = params.get('from') || '';
        var to   = params.get('to')   || '';
        var f = new FormData();
        f.append('function', 'getCreditStatement');
        f.append('userId', sessionStorage.getItem('userId') || '');
        if (from) f.append('from', from);
        if (to)   f.append('to', to);
        var r = await execute(f, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.stmt = r.data.data;
        }

        // Auto-mint a verification share token (90-day) so the QR resolves to a live Passport.
        var f2 = new FormData();
        f2.append('function', 'createPassportShareToken');
        f2.append('userId', sessionStorage.getItem('userId') || '');
        f2.append('label', 'Statement ' + this.statementId);
        f2.append('days', 90);
        var r2 = await execute(f2, flocker);
        if (r2.data && r2.data.success && r2.data.data) {
          this.shareToken = r2.data.data.token;
        }
      } catch (e) { console.error('load stmt', e); }
      this.loading = false;
      setTimeout(function(){ document.title = 'Flockr Credit Statement'; }, 200);
    },
    doPrint() { window.print(); },
    fmt(n) { return new Intl.NumberFormat('en-KE').format(Math.round(Number(n) || 0)); },
    fmtDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' }); },
    fmtDateTime(d) { if (!d) return ''; return new Date(d).toLocaleString('en-KE', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }); },
    statusClass(s) {
      if (s === 'completed' || s === 'delivered' || s === 'paid') return 'cs-badge cs-badge-ok';
      if (s === 'cancelled' || s === 'refunded') return 'cs-badge cs-badge-bad';
      return 'cs-badge cs-badge-neutral';
    }
  }
};
</script>

<style scoped>
.cs-page { background:#F7F5F2; min-height:100vh; padding:20px 12px 60px; }
.cs-toolbar { max-width:900px; margin:0 auto 12px; }
.cs-doc { background:#FFFFFF; max-width:900px; margin:0 auto; padding:32px; box-shadow:0 4px 20px rgba(0,0,0,.05); color:#1A1A1A; font-family:'Helvetica Neue', Arial, sans-serif; font-size:12px; }

.cs-head { display:flex; justify-content:space-between; gap:20px; border-bottom:2px solid #BFA164; padding-bottom:14px; margin-bottom:16px; }
.cs-brand-block { display:flex; gap:12px; align-items:center; }
.cs-brand-logo { width:52px; height:52px; object-fit:contain; }
.cs-farm { font-size:15px; font-weight:800; letter-spacing:1px; }
.cs-meta { font-size:10px; color:#6B6356; }
.cs-meta-sm { font-size:9px; }
.cs-title-block { text-align:right; }
.cs-score-big { font-size:44px; font-weight:800; line-height:1; }
.cs-tier { display:inline-block; padding:3px 12px; border-radius:20px; color:#fff; font-weight:700; font-size:11px; margin-top:2px; }

.cs-holder { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px 16px; padding:12px; background:#F7F5F2; border-radius:8px; margin-bottom:14px; }
.cs-holder div { font-size:11px; }
.cs-l { display:block; color:#6B6356; font-size:9px; text-transform:uppercase; letter-spacing:.5px; }
.cs-holder strong { font-size:12px; }

.cs-section { font-size:13px; font-weight:800; margin:18px 0 8px; padding:5px 8px; background:#F7F5F2; border-left:3px solid #BFA164; color:#1A1A1A; }

.cs-mini, .cs-data { width:100%; border-collapse:collapse; }
.cs-mini td { border:1px solid #E8E2DA; padding:6px 8px; font-size:11px; vertical-align:top; }
.cs-mini strong { display:block; font-size:9px; color:#6B6356; text-transform:uppercase; letter-spacing:.5px; font-weight:600; }
.cs-fill { display:block; font-weight:600; color:#1A1A1A; margin-top:2px; font-size:12px; }

.cs-bar { height:8px; background:#EDE7DE; border-radius:6px; overflow:hidden; }
.cs-bar-fill { height:100%; border-radius:6px; }

.cs-highlights { display:grid; grid-template-columns:repeat(3, 1fr); gap:8px; padding:10px; background:#FAF8F4; border-radius:8px; margin:8px 0 4px; }
.cs-highlights div { font-size:11px; }
.cs-highlights strong { font-size:12px; }

.cs-data th { background:#1A1A1A; color:#F5F0E8; padding:6px 8px; font-size:10px; text-align:left; text-transform:uppercase; letter-spacing:.5px; }
.cs-data td { border-bottom:1px solid #F0EBE4; padding:5px 8px; font-size:11px; }
.cs-badge { padding:2px 6px; border-radius:4px; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
.cs-badge-ok { background:rgba(22,163,74,.12); color:#16a34a; }
.cs-badge-bad { background:rgba(220,38,38,.12); color:#dc2626; }
.cs-badge-neutral { background:rgba(107,99,86,.12); color:#6B6356; }
.text-end { text-align:right; }

.cs-attest { display:flex; gap:20px; margin-top:20px; padding:12px; background:#FAF8F4; border-radius:8px; align-items:center; }
.cs-attest-body { flex:1; font-size:11px; }
.cs-attest-body p { margin:0 0 6px; }
.cs-qr { text-align:center; }
.cs-qr img { width:100px; height:100px; }
.cs-qr-hint { font-size:9px; color:#6B6356; margin-top:2px; }

.cs-foot { display:flex; justify-content:space-between; font-size:9px; color:#6B6356; margin-top:16px; padding-top:8px; border-top:1px solid #E8E2DA; }

@media print {
  .no-print { display:none !important; }
  .cs-page { background:#FFFFFF; padding:0; }
  .cs-doc { box-shadow:none; max-width:100%; padding:12mm; }
  .cs-section { page-break-after:avoid; }
  table { page-break-inside:auto; }
  tr { page-break-inside:avoid; page-break-after:auto; }
  thead { display:table-header-group; }
}
@page { size: A4; margin: 10mm; }
</style>
